<template>
    <right-filter-drawer
        v-model="showFilterDrawer"
        :title="title"
        :disable-apply="isFiltersEmpty || !isFormValid"
        :disable-clear="isFiltersEmpty"
        @cancel="onCancelFilters"
        @apply="onApplyFilters"
        @clear="onClearFilters"
    >
        <template v-slot:content>
            <v-form ref="form" v-model="isFormValid">
                <form-generator
                    v-model="localFilters"
                    :schema="schema"
                    value-class=""
                ></form-generator>
            </v-form>
        </template>
    </right-filter-drawer>
</template>

<script>
import FormLayout from '@/components/FormLayout/VerticalFormLayout';
import HocFormGenerator from '@/components/FormGenerator/HocFormGenerator';
import RightFilterDrawer from '@/components/Drawers/RightFilterDrawer';
import filtersMixin from '@/mixins/filtersDrawerMixin';
import filtersSchema from './filtersSchema';
import { mapState } from 'vuex';
import { isEmpty } from '@/common/helpers';
import { sortBy, uniq } from 'lodash';

const FormGenerator = HocFormGenerator(FormLayout);

export default {
    name: 'SpaceFilters',
    components: {
        'form-generator': FormGenerator,
        'right-filter-drawer': RightFilterDrawer
    },
    mixins: [filtersMixin],
    props: {
        filters: {
            type: Object,
            required: false,
            default: () => {}
        },
        title: {
            type: String,
            required: false,
            default: 'Space filters'
        }
    },
    computed: {
        ...mapState('spaces', ['allSpaces']),
        ...mapState('tags', ['spaceTags']),
        ...mapState('devices', ['deviceStatusSummary']),
        ...mapState('venue', ['selectedVenueInfo']),
        buildingOptions() {
            if (this.allSpaces && this.allSpaces.length) {
                let dataSource = this.allSpaces.filter(
                    item => !isEmpty(item.building)
                );
                return sortBy(
                    uniq(
                        dataSource.map(item => this.getBuildingOptionItem(item))
                    ),
                    'text'
                );
            }
            return [];
        },
        wingOptions() {
            if (this.allSpaces && this.allSpaces.length) {
                let dataSource = this.allSpaces.filter(
                    item => !isEmpty(item.wing)
                );
                if (this.localFilters.building) {
                    dataSource = dataSource.filter(item => {
                        return this.isSpaceMatch(
                            item,
                            this.localFilters.building
                        );
                    });
                }
                return sortBy(
                    uniq(dataSource.map(item => this.getWingOptionItem(item))),
                    'text'
                );
            }
            return [];
        },
        floorOptions() {
            if (this.allSpaces && this.allSpaces.length) {
                let dataSource = this.allSpaces.filter(
                    item => !isEmpty(item.floor)
                );
                if (this.localFilters.building || this.localFilters.wing) {
                    dataSource = dataSource.filter(item => {
                        return this.isSpaceMatch(
                            item,
                            this.localFilters.building,
                            this.localFilters.wing
                        );
                    });
                }
                return sortBy(
                    uniq(dataSource.map(item => this.getFloorOptionItem(item))),
                    'text'
                );
            }
            return [];
        },
        roomOptions() {
            if (this.allSpaces && this.allSpaces.length) {
                let dataSource = this.allSpaces.filter(
                    item => !isEmpty(item.room)
                );
                if (
                    this.localFilters.building ||
                    this.localFilters.wing ||
                    this.localFilters.floor
                ) {
                    dataSource = this.allSpaces.filter(item => {
                        return this.isSpaceMatch(
                            item,
                            this.localFilters.building,
                            this.localFilters.wing,
                            this.localFilters.floor
                        );
                    });
                }
                return sortBy(
                    uniq(dataSource.map(item => this.getRoomOptionItem(item))),
                    'text'
                );
            }
            return [];
        },
        tagsOptions() {
            return sortBy(this.spaceTags, 'name');
        },
        healthStatusOptions() {
            if (!isEmpty(this.deviceStatusSummary)) {
                return Object.keys(this.deviceStatusSummary).sort();
            }
            return [];
        },
        visibilityOptions() {
            return [
                { text: 'Private', value: 'PRIVATE' },
                { text: 'Public', value: 'PUBLIC' }
            ];
        },
        occupiedStatusOptions() {
            return [
                { text: 'Occupied', value: 'Yes' },
                { text: 'Unoccupied', value: 'No' }
            ];
        },
        hasDevicesOptions() {
            return [
                { text: 'Yes', value: true },
                { text: 'No', value: false }
            ];
        },
        defaultPublicChannelMapId() {
            if (this.selectedVenueInfo) {
                return this.selectedVenueInfo.defaultPublicChannelMapId;
            }
            return '';
        },
        defaultPrivateChannelMapId() {
            if (this.selectedVenueInfo) {
                return this.selectedVenueInfo.defaultPrivateChannelMapId;
            }
            return '';
        },
        defaultChannelMapOptions() {
            let options = [];
            if (this.defaultPublicChannelMapId) {
                options.push({
                    text: 'Public channel map overridden',
                    value: this.defaultPublicChannelMapId
                });
            }
            if (this.defaultPrivateChannelMapId) {
                options.push({
                    text: 'Private channel map overridden',
                    value: this.defaultPrivateChannelMapId
                });
            }
            return options;
        },
        schema() {
            // need to populate the select items list for the filters schema
            const updatedSchema = [...filtersSchema];

            const buildingField = updatedSchema.find(
                item => item.name === 'building'
            );
            buildingField.component.props.items = this.buildingOptions;

            const wingField = updatedSchema.find(item => item.name === 'wing');
            wingField.component.props.items = this.wingOptions;

            const floorField = updatedSchema.find(
                item => item.name === 'floor'
            );
            floorField.component.props.items = this.floorOptions;

            const roomField = updatedSchema.find(item => item.name === 'room');
            roomField.component.props.items = this.roomOptions;

            const tagsField = updatedSchema.find(
                item => item.name === 'vmsTags.name'
            );
            tagsField.component.props.items = this.tagsOptions;

            const healthField = updatedSchema.find(
                item => item.name === 'serviceAccount.devices.healthCheckStatus'
            );
            healthField.component.props.items = this.healthStatusOptions;

            const visibilityField = updatedSchema.find(
                item => item.name === 'visibility'
            );
            visibilityField.component.props.items = this.visibilityOptions;

            const occupiedField = updatedSchema.find(
                item => item.name === 'occupiedStatus'
            );
            occupiedField.component.props.items = this.occupiedStatusOptions;
            const hasDevicesFilter = updatedSchema.find(
                item => item.name === 'hasDevices'
            );
            hasDevicesFilter.component.props.items = this.hasDevicesOptions;
            const venueChannelMapFilter = updatedSchema.find(
                item => item.name === 'serviceAccount.notVenueChannelMapId'
            );
            venueChannelMapFilter.component.props.items = this.defaultChannelMapOptions;

            return updatedSchema;
        }
    },
    methods: {
        isSpaceMatch(item, building, wing, floor, room) {
            let match = true;
            if (building) {
                match = match && item.building === building;
            }
            if (wing) {
                match = match && item.wing === wing;
            }
            if (floor) {
                match = match && item.floor === floor;
            }
            if (room) {
                match = match && item.room === room;
            }
            return match;
        },
        getRoomOptionItem(item) {
            return {
                text: `${item.room} @ ${item.floor} FLR`,
                value: item.room
            };
        },
        getFloorOptionItem(item) {
            return {
                text: `${item.floor} FLR${
                    item.building ? '@ ' + item.building + ' BLD' : ''
                }`,
                value: item.floor
            };
        },
        getWingOptionItem(item) {
            return {
                text: `${item.wing} WING${
                    item.building ? '@ ' + item.building + ' BLD' : ''
                }`,
                value: item.wing
            };
        },
        getBuildingOptionItem(item) {
            return {
                text: `${item.building} BLD`,
                value: item.building
            };
        }
    }
};
</script>

<style></style>
